import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import {
  applyAvatarAiJob,
  fetchAvatarAiAvatars,
  fetchAvatarAiCredits,
  fetchAvatarAiThemes,
  pollAvatarAiJob,
  requestAvatarAiSourceUploadUrl,
  submitAvatarAiJob,
  uploadAvatarAiSource,
} from "../api/avatarAi";
import {
  AvatarAiCaptureInstruction,
  AvatarAiCaptureUploadBadge,
  AvatarAiCountdownOverlay,
  AvatarAiJobTile,
  AvatarAiPlayfulLoader,
  AvatarAiThemeSkeleton,
} from "./avatarAiShared";
import { useAvatarAiCameraCapture } from "../hooks/useAvatarAiCameraCapture";
import {
  AVATAR_AI_ACCEPTED_TYPES as ACCEPTED_TYPES,
  AVATAR_AI_JOB_STATUS as JOB_STATUS,
  AVATAR_AI_MAX_FILE_BYTES as MAX_FILE_BYTES,
  AVATAR_AI_POLL_INTERVAL_MS as POLL_INTERVAL_MS,
  AVATAR_AI_POLL_MAX_MS as POLL_MAX_MS,
  AVATAR_AI_QUIP_ROTATE_MS as QUIP_ROTATE_MS,
  AVATAR_AI_STEPS as STEPS,
  MAX_AVATAR_AI_PARALLEL_JOBS as MAX_PARALLEL_JOBS,
  buildAvatarAiQuipsForTheme,
  getAvatarAiFileSizeBucket,
  isAvatarAiJobTerminal,
} from "../lib/avatarAiFlow";
import {
  avatarAiPreviewGenderOptions,
  getAvatarAiThemeDescription,
  getAvatarAiThemeLabel,
  getAvatarAiThemeThumbnailUrl,
} from "../lib/avatarAiThemePreviews";

const TABS = [
  { id: "existing", label: "Avatar attuale", icon: "mdi:image-account" },
  { id: "upload", label: "Carica file", icon: "mdi:cloud-upload-outline" },
  { id: "camera", label: "Scatta foto", icon: "mdi:camera-outline" },
  { id: "saved", label: "La tua libreria", icon: "mdi:history" },
];

const isJobTerminal = isAvatarAiJobTerminal;

const AvatarAiModal = ({
  open,
  onClose,
  token,
  currentAvatarUrl,
  onApplied,
  onAvatarReplaced,
  onAvatarRemoved,
  onBuyCredits,
  onTrack,
}) => {
  const [activeTab, setActiveTab] = useState("existing");
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const [previewGender, setPreviewGender] = useState("male");
  const [credits, setCredits] = useState(null);
  const [selectedThemeIds, setSelectedThemeIds] = useState([]);
  const [step, setStep] = useState(STEPS.PICKING);
  const [error, setError] = useState("");
  const [uploadedKey, setUploadedKey] = useState("");
  const [uploadedAssetUrl, setUploadedAssetUrl] = useState("");
  const [uploadedPreview, setUploadedPreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [jobs, setJobs] = useState({});
  const [chosenThemeId, setChosenThemeId] = useState("");
  const [savedAvatars, setSavedAvatars] = useState([]);
  const [savedAvatarsLoading, setSavedAvatarsLoading] = useState(false);
  const [savedAvatarsError, setSavedAvatarsError] = useState("");
  const [selectedSavedJobId, setSelectedSavedJobId] = useState("");
  const [quipIndex, setQuipIndex] = useState(0);
  const [pollNow, setPollNow] = useState(0);
  const [applying, setApplying] = useState(false);
  const fileInputRef = useRef(null);
  const capturedFileHandlerRef = useRef(null);
  const openedTrackedRef = useRef(false);
  const generationCompletedTrackedRef = useRef(false);
  const {
    cameraActive,
    cameraAvailable,
    countdown,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    startCountdown,
  } = useAvatarAiCameraCapture({
    enabled: open,
    onCapture: (file) => capturedFileHandlerRef.current?.(file),
    onError: (err) => setError(err.message || "Camera non disponibile."),
    onTrack,
  });

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const resetState = useCallback(() => {
    setStep(STEPS.PICKING);
    setError("");
    setSelectedThemeIds([]);
    setUploadedKey("");
    setUploadedAssetUrl("");
    setUploadedPreview("");
    setUploadProgress(0);
    setJobs({});
    setChosenThemeId("");
    setApplying(false);
    setActiveTab("existing");
    setSavedAvatars([]);
    setSavedAvatarsLoading(false);
    setSavedAvatarsError("");
    setSelectedSavedJobId("");
    stopCamera();
  }, [stopCamera]);

  useEffect(() => {
    if (!open) {
      openedTrackedRef.current = false;
      generationCompletedTrackedRef.current = false;
      resetState();
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setThemesLoading(true);
      try {
        const [themesData, creditsData] = await Promise.all([
          fetchAvatarAiThemes({ token }),
          fetchAvatarAiCredits({ token }),
        ]);
        if (cancelled) return;
        setThemes(themesData.themes || []);
        setCredits(creditsData);
        if (!openedTrackedRef.current) {
          openedTrackedRef.current = true;
          onTrack?.("avatar_ai_modal_opened", {
            remainingCredits: creditsData?.remaining ?? null,
            eligible: Boolean(creditsData?.eligible),
            hasCurrentAvatar: Boolean(currentAvatarUrl),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setThemesLoading(false);
        }
      }
    };
    load();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, token, resetState, stopCamera, currentAvatarUrl, onTrack]);

  useEffect(() => {
    if (!open || activeTab !== "saved") return undefined;
    let cancelled = false;
    const loadSaved = async () => {
      setSavedAvatarsLoading(true);
      setSavedAvatarsError("");
      try {
        const data = await fetchAvatarAiAvatars({ token });
        if (cancelled) return;
        setSavedAvatars(data.avatars || []);
        onTrack?.("avatar_ai_saved_library_opened", {
          avatarsCount: (data.avatars || []).length,
        });
      } catch (err) {
        if (!cancelled) setSavedAvatarsError(err.message);
      } finally {
        if (!cancelled) setSavedAvatarsLoading(false);
      }
    };
    loadSaved();
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, token, onTrack]);

  const tabIsReady = useMemo(() => {
    if (activeTab === "existing") return Boolean(currentAvatarUrl);
    if (activeTab === "upload" || activeTab === "camera")
      return Boolean(uploadedKey);
    return false;
  }, [activeTab, currentAvatarUrl, uploadedKey]);

  const selectedCount = selectedThemeIds.length;
  const parallelLimit = Math.max(
    0,
    Math.min(MAX_PARALLEL_JOBS, credits?.remaining ?? 0),
  );
  const canSubmit =
    step === STEPS.PICKING &&
    selectedCount > 0 &&
    selectedCount <= parallelLimit &&
    tabIsReady &&
    Boolean(credits?.eligible) &&
    (credits?.remaining ?? 0) >= selectedCount;

  const toggleTheme = (themeId) => {
    setSelectedThemeIds((current) => {
      if (current.includes(themeId)) {
        const next = current.filter((id) => id !== themeId);
        onTrack?.("avatar_ai_theme_selected", {
          themeId,
          selected: false,
          selectedCount: next.length,
        });
        return next;
      }
      if (current.length >= parallelLimit) {
        return current;
      }
      const next = [...current, themeId];
      onTrack?.("avatar_ai_theme_selected", {
        themeId,
        selected: true,
        selectedCount: next.length,
      });
      return next;
    });
  };

  const handlePreviewGenderChange = (gender) => {
    setPreviewGender(gender);
    onTrack?.("avatar_ai_preview_gender_selected", { gender });
  };

  const handleFileChosen = async (file) => {
    if (!file) return;
    setError("");
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Formato non supportato. Usa JPG, PNG o WebP.");
      onTrack?.("avatar_ai_source_upload_failed", {
        source: activeTab === "camera" ? "camera" : "upload",
        reason: "unsupported_type",
        fileType: file.type || "unknown",
        fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
      });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("File troppo grande, max 5MB.");
      onTrack?.("avatar_ai_source_upload_failed", {
        source: activeTab === "camera" ? "camera" : "upload",
        reason: "file_too_large",
        fileType: file.type || "unknown",
        fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
      });
      return;
    }
    setUploadedPreview(URL.createObjectURL(file));
    onTrack?.("avatar_ai_source_upload_started", {
      source: activeTab === "camera" ? "camera" : "upload",
      fileType: file.type || "unknown",
      fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
    });
    try {
      setUploadProgress(0);
      const descriptor = await requestAvatarAiSourceUploadUrl({ token, file });
      await uploadAvatarAiSource({
        uploadDescriptor: descriptor,
        file,
        onProgress: setUploadProgress,
      });
      setUploadedKey(descriptor.key);
      setUploadedAssetUrl(descriptor.assetUrl || "");
      onTrack?.("avatar_ai_source_upload_completed", {
        source: activeTab === "camera" ? "camera" : "upload",
        fileType: file.type || "unknown",
        fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
      });
    } catch (err) {
      setError(err.message);
      onTrack?.("avatar_ai_source_upload_failed", {
        source: activeTab === "camera" ? "camera" : "upload",
        reason: "upload_failed",
        fileType: file.type || "unknown",
        fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
      });
    }
  };

  capturedFileHandlerRef.current = handleFileChosen;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError("");
    setStep(STEPS.SUBMITTING);
    const sourceType = activeTab === "existing" ? "existing" : "uploaded";
    generationCompletedTrackedRef.current = false;
    onTrack?.("avatar_ai_generation_started", {
      sourceType,
      themeIds: selectedThemeIds,
      selectedCount,
      creditsBefore: credits?.remaining ?? null,
    });
    const submissions = await Promise.allSettled(
      selectedThemeIds.map((themeId) =>
        submitAvatarAiJob({
          token,
          themeId,
          sourceType,
          sourceKey: uploadedKey,
          modelId: "model-a",
        }).then((res) => ({ themeId, jobId: res.jobId })),
      ),
    );

    const initialJobs = {};
    let acceptedCount = 0;
    const fatalErrors = [];

    submissions.forEach((result, idx) => {
      const themeId = selectedThemeIds[idx];
      if (result.status === "fulfilled") {
        initialJobs[themeId] = {
          jobId: result.value.jobId,
          status: JOB_STATUS.IN_QUEUE,
          resultUrl: "",
          errorMessage: "",
        };
        acceptedCount += 1;
      } else {
        const message = result.reason?.message || "Submit fallito.";
        initialJobs[themeId] = {
          jobId: "",
          status: JOB_STATUS.FAILED,
          resultUrl: "",
          errorMessage: message,
        };
        fatalErrors.push(`${themeId}: ${message}`);
      }
    });

    setJobs(initialJobs);

    if (acceptedCount === 0) {
      setError(fatalErrors[0] || "Nessun job accettato. Riprova fra poco.");
      onTrack?.("avatar_ai_generation_submit_failed", {
        selectedCount,
        acceptedCount,
        failedCount: selectedCount,
      });
      setStep(STEPS.ERROR);
      return;
    }

    if (fatalErrors.length) {
      onTrack?.("avatar_ai_generation_submit_failed", {
        selectedCount,
        acceptedCount,
        failedCount: fatalErrors.length,
      });
    }

    setCredits((current) =>
      current
        ? {
            ...current,
            remaining: Math.max(0, (current.remaining ?? 0) - acceptedCount),
          }
        : current,
    );
    setPollStartedAt(Date.now());
    setStep(STEPS.RUNNING);
  };

  const allTerminal = useMemo(
    () =>
      Object.values(jobs).length > 0 &&
      Object.values(jobs).every((job) => isJobTerminal(job.status)),
    [jobs],
  );

  const completedCount = useMemo(
    () =>
      Object.values(jobs).filter((job) => job.status === JOB_STATUS.COMPLETED)
        .length,
    [jobs],
  );

  useEffect(() => {
    if (step !== STEPS.RUNNING) return undefined;

    let active = true;
    const tick = async () => {
      if (!active) return;
      const pendingEntries = Object.entries(jobs).filter(
        ([, job]) => job.jobId && !isJobTerminal(job.status),
      );

      if (pendingEntries.length === 0) {
        return;
      }

      const updates = await Promise.allSettled(
        pendingEntries.map(([themeId, job]) =>
          pollAvatarAiJob({ token, jobId: job.jobId }).then((data) => ({
            themeId,
            data,
          })),
        ),
      );

      if (!active) return;

      setJobs((current) => {
        const next = { ...current };
        updates.forEach((result, idx) => {
          const [themeId] = pendingEntries[idx];
          const existing = next[themeId];
          if (!existing) return;
          if (result.status === "fulfilled") {
            const data = result.value.data;
            next[themeId] = {
              ...existing,
              status: data.status || existing.status,
              resultUrl: data.resultUrl || existing.resultUrl,
              errorMessage: data.errorMessage || existing.errorMessage,
            };
          } else {
            next[themeId] = {
              ...existing,
              errorMessage: result.reason?.message || existing.errorMessage,
            };
          }
        });
        return next;
      });

      const elapsed = Date.now() - pollStartedAt;
      if (elapsed > POLL_MAX_MS) {
        setJobs((current) => {
          const next = { ...current };
          Object.keys(next).forEach((themeId) => {
            if (!isJobTerminal(next[themeId].status)) {
              next[themeId] = {
                ...next[themeId],
                status: JOB_STATUS.FAILED,
                errorMessage: "Timeout AI.",
              };
            }
          });
          return next;
        });
        onTrack?.("avatar_ai_generation_timeout", {
          selectedCount,
          durationMs: elapsed,
        });
        return;
      }

      if (active) {
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    const t = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [step, jobs, token, pollStartedAt, onTrack, selectedCount]);

  useEffect(() => {
    if (
      step !== STEPS.RUNNING ||
      generationCompletedTrackedRef.current ||
      !allTerminal ||
      !Object.keys(jobs).length
    ) {
      return;
    }

    generationCompletedTrackedRef.current = true;
    const jobValues = Object.values(jobs);
    onTrack?.("avatar_ai_generation_completed", {
      selectedCount: jobValues.length,
      completedCount: jobValues.filter(
        (job) => job.status === JOB_STATUS.COMPLETED,
      ).length,
      failedCount: jobValues.filter((job) => job.status === JOB_STATUS.FAILED)
        .length,
      durationMs: pollStartedAt ? Date.now() - pollStartedAt : null,
    });
  }, [allTerminal, jobs, onTrack, pollStartedAt, step]);

  const quips = useMemo(() => buildAvatarAiQuipsForTheme(""), []);

  useEffect(() => {
    if (step !== STEPS.RUNNING && step !== STEPS.SUBMITTING) return undefined;
    setQuipIndex(0);
    const id = setInterval(() => {
      setQuipIndex((i) => (i + 1) % quips.length);
    }, QUIP_ROTATE_MS);
    return () => clearInterval(id);
  }, [step, quips.length]);

  useEffect(() => {
    if (step !== STEPS.RUNNING) return undefined;
    const id = setInterval(() => setPollNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [step]);

  const fakeProgressPct = useMemo(() => {
    if (step !== STEPS.RUNNING || !pollStartedAt) return 0;
    const elapsed = Math.max(0, pollNow - pollStartedAt);
    const asymptotic = 1 - Math.exp(-elapsed / 18000);
    return Math.min(95, Math.round(asymptotic * 100));
  }, [step, pollStartedAt, pollNow]);

  const handleApply = async () => {
    const job = jobs[chosenThemeId];
    if (!job?.jobId || job.status !== JOB_STATUS.COMPLETED) return;
    setError("");
    setApplying(true);
    try {
      const data = await applyAvatarAiJob({ token, jobId: job.jobId });
      setStep(STEPS.APPLIED);
      onTrack?.("avatar_ai_avatar_applied", {
        source: "generated",
        themeId: chosenThemeId,
        jobId: job.jobId,
      });
      onApplied?.(data.avatarUrl, data.card);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleApplySaved = async () => {
    if (!selectedSavedJobId) return;
    setError("");
    setApplying(true);
    try {
      const data = await applyAvatarAiJob({
        token,
        jobId: selectedSavedJobId,
      });
      setStep(STEPS.APPLIED);
      onTrack?.("avatar_ai_saved_avatar_applied", {
        source: "saved",
        jobId: selectedSavedJobId,
      });
      onApplied?.(data.avatarUrl, data.card);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleSetAsAvatar = () => {
    if (!uploadedAssetUrl) return;
    onTrack?.("avatar_ai_uploaded_avatar_applied", {
      source: activeTab === "camera" ? "camera" : "upload",
    });
    onAvatarReplaced?.(uploadedAssetUrl);
    setStep(STEPS.APPLIED);
  };

  const handleRemoveAvatar = () => {
    const confirmed = window.confirm(
      "Sei sicuro di voler rimuovere l'avatar attuale?",
    );
    if (!confirmed) return;
    onTrack?.("avatar_ai_current_avatar_removed", {
      hadCurrentAvatar: Boolean(currentAvatarUrl),
    });
    onAvatarRemoved?.();
    onClose?.();
  };

  const handleRetry = () => {
    setJobs({});
    setChosenThemeId("");
    setSelectedThemeIds([]);
    setStep(STEPS.PICKING);
  };

  if (!open) return null;

  const sourcePreviewSrc =
    activeTab === "existing" ? currentAvatarUrl : uploadedPreview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="relative w-full max-w-6xl max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={() => {
            onTrack?.("avatar_ai_modal_closed", {
              step,
              activeTab,
            });
            onClose?.();
          }}
          className="absolute right-4 top-4 z-10 text-gray-400 hover:text-gray-600"
          aria-label="Chiudi"
        >
          <Icon icon="mdi:close" className="h-6 w-6" />
        </button>

        <div className="shrink-0 px-6 pt-6">
          <div className="flex items-center gap-3 pb-4 border-b">
            <Icon icon="mdi:auto-fix" className="h-7 w-7 text-fuchsia-600" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Genera avatar con AI
              </h2>
              <p className="text-sm text-slate-500">
                Scegli sorgente e tema. La generazione costa 1 credito.
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {credits && (
                <span className="rounded-full bg-fuchsia-50 px-3 py-1 text-sm font-semibold text-fuchsia-700">
                  {credits.remaining ?? 0} crediti
                </span>
              )}
              {onBuyCredits && credits?.eligible && (
                <button
                  type="button"
                  onClick={() => {
                    onTrack?.("avatar_ai_buy_credits_clicked", {
                      source: "modal_header",
                      remainingCredits: credits?.remaining ?? null,
                    });
                    onClose?.();
                    onBuyCredits();
                  }}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white hover:bg-emerald-700"
                >
                  <Icon icon="mdi:gift-outline" className="text-base" />
                  Ottieni crediti
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {activeTab !== "saved" && (
            <p className="mt-2 text-xs text-slate-400">
              Seleziona fino a {parallelLimit}{" "}
              {parallelLimit === 1 ? "tema" : "temi"}: 1 credito ognuno,
              generati in parallelo.
            </p>
          )}
          {activeTab === "saved" && (
            <p className="mt-2 text-xs text-slate-400">
              Riapplica gratuitamente uno degli avatar AI già generati per
              questa card.
            </p>
          )}

          {credits && !credits.eligible && (
            <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              Funzione AI riservata al piano Plus.
            </div>
          )}

          {credits?.eligible && credits.remaining === 0 && (
            <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="flex-1">
                Crediti AI esauriti. Sblocca altri crediti per continuare.
              </span>
              {onBuyCredits && (
                <button
                  type="button"
                  onClick={() => {
                    onTrack?.("avatar_ai_buy_credits_clicked", {
                      source: "empty_credits_notice",
                      remainingCredits: credits?.remaining ?? null,
                    });
                    onClose?.();
                    onBuyCredits();
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                >
                  <Icon icon="mdi:gift-outline" className="text-base" />
                  Ottieni altri crediti
                </button>
              )}
            </div>
          )}

          {step === STEPS.PICKING && (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                {TABS.map((tab) => {
                  const disabled = tab.id === "camera" && !cameraAvailable;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      disabled={disabled}
                      title={
                        disabled
                          ? "Camera non disponibile su questo dispositivo"
                          : undefined
                      }
                      onClick={() => {
                        setActiveTab(tab.id);
                        setError("");
                        onTrack?.("avatar_ai_tab_selected", { tab: tab.id });
                        if (tab.id !== "camera") stopCamera();
                      }}
                      className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border ${
                        disabled
                          ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                          : activeTab === tab.id
                            ? "bg-fuchsia-600 text-white border-fuchsia-600"
                            : "bg-white text-slate-700 border-slate-200"
                      }`}
                    >
                      <Icon icon={tab.icon} className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === "saved" && (
                <SavedAvatarsGallery
                  avatars={savedAvatars}
                  loading={savedAvatarsLoading}
                  error={savedAvatarsError}
                  selectedJobId={selectedSavedJobId}
                  onSelect={setSelectedSavedJobId}
                  onTrack={onTrack}
                />
              )}

              {activeTab !== "saved" && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(280px,360px),1fr] gap-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase font-semibold tracking-wide text-slate-500 mb-2">
                      Sorgente
                    </p>

                    {activeTab === "existing" && (
                      <div className="flex items-center justify-center bg-slate-50 rounded-lg aspect-square overflow-hidden">
                        {currentAvatarUrl ? (
                          <img
                            src={currentAvatarUrl}
                            alt="Avatar attuale"
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <span className="text-sm text-slate-400">
                            Nessun avatar attuale
                          </span>
                        )}
                      </div>
                    )}

                    {activeTab === "upload" && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center bg-slate-50 rounded-lg aspect-square overflow-hidden">
                          {uploadedPreview ? (
                            <img
                              src={uploadedPreview}
                              alt="Preview"
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-sm text-slate-400">
                              Nessun file
                            </span>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ACCEPTED_TYPES.join(",")}
                          className="hidden"
                          onChange={(e) =>
                            handleFileChosen(e.target.files?.[0])
                          }
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full rounded-lg border border-dashed border-slate-300 py-2 text-sm text-slate-600 hover:bg-slate-50"
                        >
                          {uploadedKey ? "Cambia file" : "Seleziona file"}
                        </button>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <div className="text-xs text-slate-500">
                            Upload {uploadProgress}%
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === "camera" && (
                      <div className="space-y-2">
                        <div className="relative flex items-center justify-center bg-slate-900 rounded-lg aspect-square overflow-hidden">
                          {uploadedPreview ? (
                            <img
                              src={uploadedPreview}
                              alt="Capture"
                              className="object-cover w-full h-full"
                            />
                          ) : cameraActive ? (
                            <video
                              ref={videoRef}
                              playsInline
                              muted
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-sm text-slate-300">
                              Camera spenta
                            </span>
                          )}
                          {cameraActive && !uploadedPreview && (
                            <AvatarAiCaptureInstruction />
                          )}
                          <AvatarAiCountdownOverlay
                            countdown={countdown}
                            label="Guarda nell'obiettivo"
                          />
                          {uploadedPreview && !uploadedKey && (
                            <AvatarAiCaptureUploadBadge
                              progress={uploadProgress}
                              label="Preparo la foto..."
                            />
                          )}
                        </div>
                        <canvas ref={canvasRef} className="hidden" />
                        {!cameraActive && !uploadedPreview && (
                          <button
                            type="button"
                            onClick={startCamera}
                            className="w-full rounded-lg bg-slate-800 py-2 text-sm text-white"
                          >
                            Accendi camera
                          </button>
                        )}
                        {cameraActive && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={startCountdown}
                              disabled={countdown !== null}
                              className={`flex-1 rounded-lg py-2 text-sm text-white ${
                                countdown !== null
                                  ? "bg-fuchsia-400 cursor-not-allowed"
                                  : "bg-fuchsia-600"
                              }`}
                            >
                              {countdown !== null
                                ? "Pronto..."
                                : "Scatta (3..2..1)"}
                            </button>
                            <button
                              type="button"
                              onClick={stopCamera}
                              className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                            >
                              Stop
                            </button>
                          </div>
                        )}
                        {uploadedPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedKey("");
                              setUploadedAssetUrl("");
                              setUploadedPreview("");
                              onTrack?.("avatar_ai_tab_selected", {
                                tab: "camera",
                                action: "retake",
                              });
                              startCamera();
                            }}
                            className="w-full rounded-lg border border-slate-200 py-2 text-sm text-slate-700"
                          >
                            Rifai foto
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Temi · selezionati {selectedCount}/{parallelLimit}
                        </p>
                        <div className="mt-2 inline-grid grid-cols-2 rounded-full bg-slate-100 p-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200">
                          {avatarAiPreviewGenderOptions.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() =>
                                handlePreviewGenderChange(option.id)
                              }
                              className={`rounded-full px-3 py-1 transition ${
                                previewGender === option.id
                                  ? "bg-slate-900 text-white shadow-sm"
                                  : "hover:text-slate-900"
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {selectedCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedThemeIds([])}
                          className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                        >
                          Pulisci
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 max-h-[42rem] overflow-y-auto pr-1">
                      {themesLoading &&
                        Array.from({ length: 9 }).map((_, idx) => (
                          <AvatarAiThemeSkeleton key={`skeleton-${idx}`} />
                        ))}
                      {!themesLoading &&
                        themes.map((theme) => {
                          const isSelected = selectedThemeIds.includes(
                            theme.id,
                          );
                          const isDisabled =
                            !isSelected && selectedCount >= parallelLimit;
                          const thumbnailUrl = getAvatarAiThemeThumbnailUrl(
                            theme,
                            previewGender,
                          );
                          const themeLabel = getAvatarAiThemeLabel(
                            theme,
                            previewGender,
                          );
                          const themeDescription = getAvatarAiThemeDescription(
                            theme,
                            previewGender,
                          );
                          return (
                            <button
                              key={theme.id}
                              type="button"
                              disabled={isDisabled}
                              onClick={() => toggleTheme(theme.id)}
                              className={`text-left rounded-lg border overflow-hidden transition ${
                                isSelected
                                  ? "border-fuchsia-600 ring-2 ring-fuchsia-200"
                                  : isDisabled
                                    ? "border-slate-200 opacity-50 cursor-not-allowed"
                                    : "border-slate-200 hover:border-slate-300"
                              }`}
                            >
                              <div className="aspect-square relative bg-slate-100">
                                {thumbnailUrl ? (
                                  <img
                                    src={thumbnailUrl}
                                    alt={themeLabel}
                                    loading="lazy"
                                    className="object-cover w-full h-full"
                                  />
                                ) : (
                                  <div className="flex items-center justify-center h-full text-slate-300">
                                    <Icon
                                      icon="mdi:image-outline"
                                      className="h-8 w-8"
                                    />
                                  </div>
                                )}
                                <span
                                  className={`absolute top-1 left-1 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${
                                    theme.type === "pro"
                                      ? "bg-emerald-600 text-white"
                                      : "bg-fuchsia-600 text-white"
                                  }`}
                                >
                                  {theme.type === "pro" ? "Pro" : "Fun"}
                                </span>
                                {isSelected && (
                                  <div className="absolute top-1 right-1 rounded-full bg-fuchsia-600 text-white p-1">
                                    <Icon
                                      icon="mdi:check"
                                      className="h-4 w-4"
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="px-3 py-2 bg-white">
                                <p className="text-sm font-semibold text-slate-900 truncate">
                                  {themeLabel}
                                </p>
                                <p className="text-[11px] text-slate-500 truncate">
                                  {themeDescription}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {step === STEPS.SUBMITTING && (
            <AvatarAiPlayfulLoader
              phase="submit"
              quip={quips[quipIndex] || "Invio..."}
              sourceSrc={sourcePreviewSrc}
            />
          )}

          {step === STEPS.RUNNING && (
            <div className="mt-4">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <Icon
                  icon={allTerminal ? "mdi:check-circle" : "mdi:auto-fix"}
                  className={`h-6 w-6 text-fuchsia-600 ${
                    allTerminal ? "" : "animate-pulse"
                  }`}
                />
                <p className="text-sm text-slate-700">
                  {allTerminal
                    ? `Pronti ${completedCount}/${selectedCount}. Scegli quale applicare.`
                    : `Generazione in corso · ${completedCount}/${selectedCount} pronti · ${quips[quipIndex] || ""}`}
                </p>
                {!allTerminal && (
                  <div className="ml-auto h-2 w-32 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 transition-all duration-500"
                      style={{ width: `${Math.max(4, fakeProgressPct)}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {selectedThemeIds.map((themeId) => {
                  const theme = themes.find((t) => t.id === themeId);
                  const job = jobs[themeId];
                  return (
                    <AvatarAiJobTile
                      key={themeId}
                      label={
                        getAvatarAiThemeLabel(theme, previewGender) || themeId
                      }
                      type={theme?.type}
                      job={job}
                      selected={chosenThemeId === themeId}
                      onSelect={() => {
                        if (job?.status === JOB_STATUS.COMPLETED) {
                          setChosenThemeId(themeId);
                          onTrack?.("avatar_ai_result_selected", {
                            themeId,
                          });
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {step === STEPS.APPLIED && (
            <Status icon="mdi:check-circle" message="Avatar aggiornato!" />
          )}

          {step === STEPS.ERROR && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error || "Errore generico."}
            </div>
          )}

          {step === STEPS.PICKING && error && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {step === STEPS.PICKING && currentAvatarUrl && onAvatarRemoved && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
              >
                <Icon icon="mdi:trash-can-outline" className="text-base" />
                Rimuovi avatar
              </button>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
              <button
                type="button"
                onClick={() => {
                  onTrack?.("avatar_ai_modal_closed", {
                    step,
                    activeTab,
                  });
                  onClose?.();
                }}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              >
                {step === STEPS.APPLIED ? "Chiudi" : "Annulla"}
              </button>
              {step === STEPS.PICKING &&
                (activeTab === "upload" || activeTab === "camera") &&
                uploadedAssetUrl &&
                onAvatarReplaced && (
                  <button
                    type="button"
                    onClick={handleSetAsAvatar}
                    className="rounded-full border border-fuchsia-200 bg-white px-4 py-2 text-sm font-semibold text-fuchsia-700 hover:bg-fuchsia-50"
                  >
                    Imposta come avatar
                  </button>
                )}
              {step === STEPS.PICKING && activeTab !== "saved" && (
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    canSubmit
                      ? "bg-fuchsia-600 text-white"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {selectedCount > 1
                    ? `Genera ${selectedCount} varianti (${selectedCount} crediti)`
                    : "Genera (1 credito)"}
                </button>
              )}
              {step === STEPS.PICKING && activeTab === "saved" && (
                <button
                  type="button"
                  disabled={!selectedSavedJobId || applying}
                  onClick={handleApplySaved}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    selectedSavedJobId && !applying
                      ? "bg-fuchsia-600 text-white"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {applying ? "Applico..." : "Applica avatar"}
                </button>
              )}
              {step === STEPS.RUNNING && (
                <>
                  {allTerminal && (
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                    >
                      Riprova
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!chosenThemeId || applying}
                    onClick={handleApply}
                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                      chosenThemeId && !applying
                        ? "bg-fuchsia-600 text-white"
                        : "bg-slate-200 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    {applying ? "Applico..." : "Applica come avatar"}
                  </button>
                </>
              )}
              {step === STEPS.ERROR && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="rounded-full bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Riprova
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Status = ({ icon, message, spinning }) => (
  <div className="mt-6 flex flex-col items-center justify-center gap-3 py-10 text-center">
    <Icon
      icon={icon}
      className={`h-10 w-10 text-fuchsia-600 ${spinning ? "animate-spin" : ""}`}
    />
    <p className="text-sm text-slate-600">{message}</p>
  </div>
);

const SavedAvatarsGallery = ({
  avatars,
  loading,
  error,
  selectedJobId,
  onSelect,
  onTrack,
}) => {
  if (loading) {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, idx) => (
            <AvatarAiThemeSkeleton key={`saved-skeleton-${idx}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!avatars.length) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
        <Icon
          icon="mdi:image-multiple-outline"
          className="mx-auto mb-2 h-10 w-10 text-slate-300"
        />
        Nessun avatar AI generato finora.
        <br />
        Genera il tuo primo avatar dagli altri tab.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-slate-200 p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {avatars.map((avatar) => {
          const isSelected = selectedJobId === avatar.jobId;
          return (
            <button
              key={avatar.jobId}
              type="button"
              onClick={() => {
                onSelect(avatar.jobId);
                onTrack?.("avatar_ai_saved_avatar_selected", {
                  jobId: avatar.jobId,
                });
              }}
              className={`text-left rounded-lg border overflow-hidden transition ${
                isSelected
                  ? "border-fuchsia-600 ring-2 ring-fuchsia-200"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="aspect-square relative bg-slate-100">
                <img
                  src={avatar.resultUrl}
                  alt={avatar.themeLabel || avatar.themeId}
                  loading="lazy"
                  className="object-cover w-full h-full"
                />
                {avatar.status === "APPLIED" && (
                  <span className="absolute top-1 left-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase px-2 py-0.5">
                    In uso
                  </span>
                )}
                {isSelected && (
                  <div className="absolute top-1 right-1 rounded-full bg-fuchsia-600 text-white p-1">
                    <Icon icon="mdi:check" className="h-4 w-4" />
                  </div>
                )}
              </div>
              <div className="px-3 py-2 bg-white">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {avatar.themeLabel || avatar.themeId}
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                  {avatar.createdAt
                    ? new Date(avatar.createdAt).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : ""}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AvatarAiModal;
